import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Code2, LoaderCircle } from "lucide-react";
import { api, ApiError } from "../../api";
import { t } from "../../i18n";
import type { Environment } from "../chat/ChatDevelopmentPanel.types";

/** Workspace runtimes are conversation-scoped; reuse this tab's editor session. */
export function WorkspaceEditorLaunchButton({
  workspaceId,
  workspaceName,
  disabled,
  onError,
}: {
  workspaceId: string;
  workspaceName: string;
  disabled?: boolean;
  onError: (message: string) => void;
}) {
  const navigate = useNavigate();
  const [opening, setOpening] = useState(false);
  const open = async () => {
    setOpening(true);
    const key = `lumen:editor-conversation:${workspaceId}`;
    try {
      let conversationId: string | null = null;
      try {
        conversationId = sessionStorage.getItem(key);
      } catch {
        /* Storage is optional. */
      }
      if (conversationId) {
        try {
          await api(`/conversations/${conversationId}`);
        } catch (reason) {
          if (
            !(reason instanceof ApiError) ||
            ![403, 404].includes(reason.status)
          )
            throw reason;
          conversationId = null;
        }
      }
      if (!conversationId) {
        const conversation = await api<{ id: string }>("/conversations", {
          method: "POST",
          body: JSON.stringify({
            title: `${workspaceName} workspace`,
            composerDefaults: {
              agentWorkspaceId: workspaceId,
              agentWorkspaceIds: [workspaceId],
            },
          }),
        });
        conversationId = conversation.id;
        // Store before configuring so retries do not create more empty chats.
        try {
          sessionStorage.setItem(key, conversationId);
        } catch {
          /* Storage is optional. */
        }
      }
      const environment = await api<Environment>(
        `/conversations/${conversationId}/development-workspace`,
      );
      const existing =
        environment.configured?.workspaceId === workspaceId
          ? environment.configured
          : environment.configurations?.find(
              (item) => item.workspaceId === workspaceId,
            );
      if (!existing) {
        await api(`/conversations/${conversationId}/development-workspace`, {
          method: "PUT",
          body: JSON.stringify({
            workspaceId,
            repositoryId: null,
            preserveSelections: true,
          }),
        });
      }
      const query = new URLSearchParams({ workspace: workspaceId });
      if (existing?.repositoryId)
        query.set("repository", existing.repositoryId);
      navigate(`/c/${conversationId}/workspace?${query}`);
    } catch (reason) {
      onError(
        reason instanceof Error ? reason.message : t("Could not open editor"),
      );
    } finally {
      setOpening(false);
    }
  };
  return (
    <button
      type="button"
      className="button shrink-0 self-center p-2"
      disabled={disabled || opening}
      onClick={() => void open()}
      title={t("Open editor · starts or resumes a workspace chat")}
      aria-label={t("Open editor for {{name}}", { name: workspaceName })}
    >
      {opening ? (
        <LoaderCircle size={16} className="animate-spin" />
      ) : (
        <Code2 size={16} />
      )}
    </button>
  );
}
